import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReferralProgram = ({ referralData, onInvite }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);

  const mockReferralData = {
    referralCode: 'WELLNESS2025',
    totalReferrals: 12,
    successfulReferrals: 8,
    pendingReferrals: 4,
    totalRewards: 450,
    availableRewards: 150,
    redeemedRewards: 300,
    referralLink: 'https://digitalwellnessbank.com/join?ref=WELLNESS2025',
    recentReferrals: [
      {
        id: 1,
        name: 'Sarah Johnson',
        email: 'sarah.j@email.com',
        status: 'completed',
        joinDate: '2025-09-28',
        reward: 50,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
      },
      {
        id: 2,
        name: 'Mike Chen',
        email: 'mike.chen@email.com',
        status: 'pending',
        inviteDate: '2025-10-05',
        reward: 50,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      },
      {
        id: 3,
        name: 'Emily Rodriguez',
        email: 'emily.r@email.com',
        status: 'completed',
        joinDate: '2025-10-01',
        reward: 50,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
      },
      {
        id: 4,
        name: 'David Kim',
        email: 'david.kim@email.com',
        status: 'pending',
        inviteDate: '2025-10-07',
        reward: 50,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
      }
    ],
    rewardTiers: [
      { referrals: 1, reward: 50, title: 'First Friend' },
      { referrals: 5, reward: 100, title: 'Social Circle' },
      { referrals: 10, reward: 200, title: 'Community Builder' },
      { referrals: 25, reward: 500, title: 'Wellness Ambassador' }
    ]
  };

  const data = referralData || mockReferralData;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(data?.referralLink);
    // Show success message (could be implemented with toast)
  };

  const handleCopyCode = () => {
    navigator.clipboard?.writeText(data?.referralCode);
    // Show success message
  };

  const handleSendInvite = () => {
    if (inviteEmail) {
      onInvite?.({
        email: inviteEmail,
        message: inviteMessage || `Join me on Digital Wellness Bank and start your wellness journey! Use my referral code: ${data?.referralCode}`
      });
      setInviteEmail('');
      setInviteMessage('');
      setShowInviteForm(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'expired': return 'XCircle';
      default: return 'Circle';
    }
  };

  const nextTier = data?.rewardTiers?.find(tier => tier?.referrals > data?.successfulReferrals);
  const progressToNext = nextTier ? (data?.successfulReferrals / nextTier?.referrals) * 100 : 100;

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="Users" size={24} className="text-primary" />
        <h2 className="text-xl font-heading font-semibold text-foreground">Referral Program</h2>
      </div>
      {/* Referral Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-primary/10 rounded-lg">
          <div className="text-2xl font-bold text-primary">{data?.totalReferrals}</div>
          <div className="text-sm text-muted-foreground">Total Invites</div>
        </div>
        <div className="text-center p-4 bg-success/10 rounded-lg">
          <div className="text-2xl font-bold text-success">{data?.successfulReferrals}</div>
          <div className="text-sm text-muted-foreground">Successful</div>
        </div>
        <div className="text-center p-4 bg-warning/10 rounded-lg">
          <div className="text-2xl font-bold text-warning">{data?.pendingReferrals}</div>
          <div className="text-sm text-muted-foreground">Pending</div>
        </div>
        <div className="text-center p-4 bg-accent/10 rounded-lg">
          <div className="text-2xl font-bold text-accent">${data?.totalRewards}</div>
          <div className="text-sm text-muted-foreground">Total Rewards</div>
        </div>
      </div>
      {/* Referral Tools */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Share & Earn</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Referral Link */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">Referral Link</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                iconName="Copy"
                iconPosition="left"
              >
                Copy
              </Button>
            </div>
            <div className="p-3 bg-background border border-border rounded-lg text-sm text-muted-foreground break-all">
              {data?.referralLink}
            </div>
          </div>

          {/* Referral Code */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">Referral Code</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyCode}
                iconName="Copy"
                iconPosition="left"
              >
                Copy
              </Button>
            </div>
            <div className="p-3 bg-background border border-border rounded-lg text-center">
              <span className="text-2xl font-mono font-bold text-primary">{data?.referralCode}</span>
            </div>
          </div>
        </div>

        {/* Invite Form */}
        <div className="mt-4">
          {!showInviteForm ? (
            <Button
              variant="default"
              onClick={() => setShowInviteForm(true)}
              iconName="Mail"
              iconPosition="left"
              fullWidth
            >
              Send Personal Invitation
            </Button>
          ) : (
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="space-y-4">
                <Input
                  label="Friend's Email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e?.target?.value)}
                  placeholder="friend@example.com"
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e?.target?.value)}
                    placeholder="Add a personal message to your invitation..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowInviteForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleSendInvite}
                    disabled={!inviteEmail}
                    className="flex-1"
                  >
                    Send Invite
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Reward Progress */}
      {nextTier && (
        <div className="mb-6">
          <h3 className="text-lg font-heading font-medium text-foreground mb-4">Next Reward Tier</h3>
          <div className="p-4 bg-accent/10 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">{nextTier?.title}</h4>
              <span className="text-sm text-muted-foreground">
                {data?.successfulReferrals}/{nextTier?.referrals} referrals
              </span>
            </div>
            <div className="w-full bg-border rounded-full h-2 mb-3">
              <div 
                className="bg-accent h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressToNext}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {nextTier?.referrals - data?.successfulReferrals} more referrals needed
              </span>
              <span className="font-medium text-accent">+${nextTier?.reward} reward</span>
            </div>
          </div>
        </div>
      )}
      {/* Recent Referrals */}
      <div className="mb-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Recent Referrals</h3>
        <div className="space-y-3">
          {data?.recentReferrals?.map((referral) => (
            <div key={referral?.id} className="flex items-center space-x-4 p-3 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                <img
                  src={referral?.avatar}
                  alt={referral?.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground truncate">{referral?.name}</h4>
                <p className="text-sm text-muted-foreground truncate">{referral?.email}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`flex items-center space-x-1 text-sm ${getStatusColor(referral?.status)}`}>
                  <Icon name={getStatusIcon(referral?.status)} size={16} />
                  <span className="capitalize">{referral?.status}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {referral?.joinDate ? 
                    `Joined ${new Date(referral.joinDate)?.toLocaleDateString()}` :
                    `Invited ${new Date(referral.inviteDate)?.toLocaleDateString()}`
                  }
                </div>
              </div>
              {referral?.status === 'completed' && (
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-medium text-success">+${referral?.reward}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Reward Summary */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Reward Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-success/10 rounded-lg text-center">
            <div className="text-2xl font-bold text-success">${data?.availableRewards}</div>
            <div className="text-sm text-muted-foreground">Available to Redeem</div>
            <Button variant="outline" size="sm" className="mt-2">
              Redeem Now
            </Button>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg text-center">
            <div className="text-2xl font-bold text-foreground">${data?.redeemedRewards}</div>
            <div className="text-sm text-muted-foreground">Already Redeemed</div>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg text-center">
            <div className="text-2xl font-bold text-primary">${data?.totalRewards}</div>
            <div className="text-sm text-muted-foreground">Total Earned</div>
          </div>
        </div>
      </div>
      {/* Program Benefits */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">How It Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Share2" size={24} color="white" />
            </div>
            <h4 className="font-medium text-foreground mb-2">1. Share</h4>
            <p className="text-sm text-muted-foreground">Send your referral link or code to friends</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="UserPlus" size={24} color="white" />
            </div>
            <h4 className="font-medium text-foreground mb-2">2. They Join</h4>
            <p className="text-sm text-muted-foreground">Friends sign up using your referral</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Gift" size={24} color="white" />
            </div>
            <h4 className="font-medium text-foreground mb-2">3. Earn Rewards</h4>
            <p className="text-sm text-muted-foreground">Get $50 for each successful referral</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;